import {
  assembleCreateRecord,
  assembleEditRecord,
  SaveAssemblyError,
  SaveSource,
  toWireValue,
  writePath,
} from '../edit-transaction/transaction-save.assembly'
import { saveCategory } from '../edit-transaction/transaction-category-wire'
import { EditTransactionField } from '../edit-transaction/edit-transaction.projection'
import { TransactionSaveRequest } from '../models/transaction-save.model'
import {
  BulkTransactionRow,
  BulkTransactionViewModel,
} from './bulk-transaction.projection'

/**
 * The most transactions one save request carries (PVAL-26440). A selection runs to tens of
 * thousands, and one request per account of that size is one the server and every proxy between
 * would have to hold whole; past this an account's rows go in several requests, sent one after
 * another.
 */
export const SAVE_CHUNK_SIZE = 500

/** One request of a bulk save, and whose it is. */
export interface BulkSaveBatch {
  /** The raw account the request is for — what the dialog names when it reports an outcome. */
  accountId: string
  request: TransactionSaveRequest
}

/** A bulk row as the record assembly reads a dialog model. */
const sourceOf = (
  row: BulkTransactionRow,
  mode: BulkTransactionViewModel['mode'],
): SaveSource => ({
  mode,
  sections: [{ name: '', fields: Object.values(row.fields) }],
  footer: [],
  collections: row.collections,
  recordId: row.recordId,
})

/**
 * The footer's values, typed — the SESSION's, written into every transaction that is sent and into
 * no other. A footer value never makes a row sent on its own: it applies to the rows that are.
 */
const footerValues = (
  footer: readonly EditTransactionField[],
): [string, unknown][] =>
  footer.flatMap(field => {
    if (!field.editable) return []
    const wire = toWireValue(field, field.rawValue)
    return wire == null ? [] : [[field.field, wire] as [string, unknown]]
  })

/**
 * Every request a bulk save sends — grouped by each row's OWN account (a selection can span
 * several, and one request names one), then chunked at {@link SAVE_CHUNK_SIZE}.
 *
 *  - `edit` — only the rows at [changedIndexes], which are projected already (an edit is what
 *    projects a row), each as `{id, …what changed}` by the typed diff. A row the diff finds nothing
 *    in is not sent, whatever the change count said.
 *  - `post` — every row, each as a new record (never an `id`). A row already projected is assembled
 *    from its projected — possibly edited — fields; one that never was, through `rawRecordAt`,
 *    without projecting it.
 *
 * @throws SaveAssemblyError naming the first value that will not type; `field` is `accountId` for a
 *         row with no usable account, and `id` for an edited row with no record id
 */
export function assembleBulkRequests(
  vm: BulkTransactionViewModel,
  changedIndexes: readonly number[],
  chunkSize = SAVE_CHUNK_SIZE,
): BulkSaveBatch[] {
  const mode = vm.mode
  const footer = footerValues(vm.footer)
  const category = saveCategory(mode, vm.tranCategory)
  const indexes =
    mode === 'edit'
      ? changedIndexes
      : Array.from({ length: vm.rowCount }, (_, index) => index)

  const byAccount = new Map<string, Record<string, unknown>[]>()
  for (const index of indexes) {
    let record: Record<string, unknown>
    if (mode === 'edit') {
      const row = vm.projectedAt(index) ?? vm.rowAt(index)
      if (!row.baseline) continue
      const edit = assembleEditRecord(sourceOf(row, mode), row.baseline)
      if (!edit.hasChanges) continue
      // An edit names the record it edits; one with no id would be refused for every row it rode with.
      if (!row.recordId) throw new SaveAssemblyError('id', row.title)
      record = edit.record
    } else {
      const row = vm.projectedAt(index)
      record = row
        ? assembleCreateRecord(sourceOf(row, mode))
        : vm.rawRecordAt(index)
    }
    footer.forEach(([field, value]) => writePath(record, field, value))

    const account = vm.accountAt(index)
    const list = byAccount.get(account) ?? []
    list.push(record)
    byAccount.set(account, list)
  }

  const batches: BulkSaveBatch[] = []
  byAccount.forEach((records, account) => {
    const accountId = Number(account)
    if (!account.trim() || !Number.isInteger(accountId)) {
      throw new SaveAssemblyError('accountId', 'Account')
    }
    for (let start = 0; start < records.length; start += chunkSize) {
      batches.push({
        accountId: account,
        request: {
          accountId,
          ...(category ? { category } : {}),
          transactions: records.slice(start, start + chunkSize),
        },
      })
    }
  })
  return batches
}
